import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { bankAPI } from '../lib/api';

function fmtMoney(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}
function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

export default function BewegungMatchModal({ open, onClose, slug, bewegung, onMatched }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !bewegung) return;
    setLoading(true);
    bankAPI.matchVorschlaege(slug, bewegung.id)
      .then((r) => setCandidates(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Vorschläge konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, bewegung, slug]);

  const accept = async (c) => {
    setBusy(true);
    try {
      await bankAPI.acceptMatch(slug, bewegung.id, {
        beleg_id: c.beleg_id || null,
        ausgang_id: c.ausgang_id || null,
      });
      toast.success('Match gebucht — Rechnung als bezahlt markiert');
      onMatched && onMatched();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Match fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const ignore = async () => {
    if (!window.confirm('Diese Bewegung wirklich als ignoriert markieren? (z.B. Privatentnahme)')) return;
    setBusy(true);
    try {
      await bankAPI.ignore(slug, bewegung.id);
      toast.success('Ignoriert');
      onMatched && onMatched();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (!bewegung) return null;
  const isOutflow = Number(bewegung.betrag) < 0;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to">
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>
        <div className="modal-positioner">
          <Transition.Child as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to">
            <Dialog.Panel className="modal-panel" style={{ width: 720 }}>
              <div className="modal-header">
                <Dialog.Title className="modal-title">Bewegung zuordnen</Dialog.Title>
                <button type="button" className="btn-text" onClick={onClose}><X size={16} /></button>
              </div>

              <div className="card" style={{ marginTop: 12, marginBottom: 12, padding: 12, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div className="muted small">{fmtDate(bewegung.buchungs_datum)} · {bewegung.bank_konto_name}</div>
                    <div style={{ fontWeight: 600 }}>{bewegung.gegenseite_name || '—'}</div>
                    {bewegung.gegenseite_iban && <div className="muted small">{bewegung.gegenseite_iban}</div>}
                    {bewegung.verwendungszweck && (
                      <div className="muted small" style={{ marginTop: 4 }}>{bewegung.verwendungszweck}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="muted small">{isOutflow ? 'Ausgang' : 'Eingang'}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: isOutflow ? '#991b1b' : '#166534' }}>
                      {isOutflow ? '-' : '+'}{fmtMoney(Math.abs(bewegung.betrag))}
                    </div>
                  </div>
                </div>
              </div>

              <h4 style={{ margin: '8px 0' }}>
                Vorschläge {isOutflow ? '(Eingangsrechnungen)' : '(Ausgangsrechnungen)'}
              </h4>
              {loading ? (
                <div className="muted">Sucht Treffer…</div>
              ) : candidates.length === 0 ? (
                <div className="card empty" style={{ padding: 12 }}>
                  Keine offene Rechnung mit passendem Betrag im +/- 60-Tage-Fenster gefunden.
                  Du kannst die Bewegung manuell ignorieren (z.B. Privatentnahme) oder erst die
                  Rechnung anlegen/buchen.
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Rechn.-Nr</th>
                      <th>Partner</th>
                      <th className="num">Offen</th>
                      <th className="num">Score</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={`${c.beleg_id || ''}-${c.ausgang_id || ''}`}>
                        <td>{fmtDate(c.beleg_datum)}</td>
                        <td><b>{c.rechnungsnummer || '—'}</b></td>
                        <td>{c.partner_name || '—'}</td>
                        <td className="num">{fmtMoney(c.offen)}</td>
                        <td className="num">
                          <span style={{
                            color: c.score >= 0.8 ? '#166534' : (c.score >= 0.6 ? '#854d0e' : '#475569'),
                            fontWeight: 600,
                          }}>{Math.round(c.score * 100)}%</span>
                        </td>
                        <td>
                          <button className="btn-primary" onClick={() => accept(c)} disabled={busy}>
                            Übernehmen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="actions" style={{ borderTop: 'none', paddingTop: 12, justifyContent: 'space-between' }}>
                <button type="button" className="btn-text" onClick={ignore} disabled={busy}
                  style={{ color: '#991b1b' }}>
                  Ignorieren
                </button>
                <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                  Schließen
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
